import { Check, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { useLanguage } from "../i18n/LanguageContext";

const HERO_VIDEO = "/assets/uba-fondo.mp4";

export const Hero = () => {
  const { t } = useLanguage();

  return (
    <section id="top" className="relative overflow-hidden" data-testid="hero-section">
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover"
        data-testid="hero-video"
      >
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0d2b52] via-[#0d2b52]/95 to-[#0d2b52]/75" aria-hidden="true" />

      <div
        className="absolute inset-x-0 bottom-8 hidden justify-center lg:inset-x-auto lg:bottom-auto lg:right-10 lg:top-1/2 lg:flex lg:-translate-y-1/2 xl:right-16"
        data-testid="hero-promo"
      >
        <div className="relative">
          <span className="absolute inset-0 animate-ping rounded-full bg-[#dc2626] opacity-25" aria-hidden="true" />
          <div className="relative flex h-44 w-44 rotate-[-6deg] flex-col items-center justify-center rounded-full bg-[#dc2626] p-5 text-center shadow-2xl lg:h-60 lg:w-60">
            <span className="font-display text-lg font-extrabold uppercase tracking-widest text-white lg:text-2xl">
              {t.hero.promo.offer}
            </span>
            <span className="font-display mt-0.5 text-xs font-bold uppercase tracking-wide text-white/90 lg:text-sm">
              {t.hero.promo.until}
            </span>
            <span className="my-1.5 h-px w-3/4 bg-white/40" aria-hidden="true" />
            <span className="font-display text-xl font-extrabold uppercase leading-tight text-white lg:text-3xl">
              {t.hero.promo.free}
            </span>
            <span className="font-display mt-1 text-xs font-semibold uppercase tracking-wide text-white/90 lg:text-sm">
              {t.hero.promo.withAny}
            </span>
          </div>
        </div>
      </div>

      <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-32 lg:px-8">
        <div className="max-w-3xl">
          <div
            className="inline-flex items-center gap-2 border border-white/20 bg-white/10 px-3 py-1.5 backdrop-blur-sm"
            data-testid="hero-badge"
          >
            <MapPin className="h-3.5 w-3.5 text-[#2f9be8]" />
            <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white">
              {t.hero.badge}
            </span>
          </div>

          <h1
            className="font-display mt-6 text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
            data-testid="hero-title"
          >
            {t.hero.title}
            <span className="block text-[#2f9be8]">{t.hero.titleAccent}</span>
          </h1>

          <div
            className="mt-6 inline-block bg-white px-5 py-2.5"
            data-testid="hero-price"
          >
            <span className="font-display text-2xl font-bold tracking-tight text-[#0d2b52] sm:text-3xl">
              {t.hero.heroPrice}
            </span>
          </div>
          <p
            className="mt-3 max-w-md text-xs leading-relaxed text-[#cbd5e1] sm:text-sm"
            data-testid="hero-price-note"
          >
            {t.pricing.priceNote}
          </p>

          <p
            className="mt-6 max-w-xl text-base leading-relaxed text-[#e2e8f0] sm:text-lg"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Button
              asChild
              size="lg"
              className="rounded-none bg-[#1d6fd1] px-8 font-display text-base font-bold text-white transition-colors hover:bg-[#2f9be8]"
              data-testid="hero-cta-primary"
            >
              <a href="#contact">{t.hero.ctaPrimary}</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-none border-white bg-transparent px-8 font-display text-base font-semibold text-white hover:bg-white/10 hover:text-white"
              data-testid="hero-cta-secondary"
            >
              <a href="#servicii">{t.hero.ctaSecondary}</a>
            </Button>
          </div>

          <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-3" data-testid="hero-chips">
            {t.hero.chips.map((chip, i) => (
              <li
                key={i}
                className="flex items-center gap-2 text-sm font-semibold text-white"
                data-testid={`hero-chip-${i}`}
              >
                <span className="flex h-5 w-5 items-center justify-center bg-[#1d6fd1]">
                  <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                </span>
                {chip}
              </li>
            ))}
          </ul>

          <div className="mt-10 flex justify-start lg:hidden" data-testid="hero-promo-mobile">
            <div className="relative">
              <span className="absolute inset-0 animate-ping rounded-full bg-[#dc2626] opacity-25" aria-hidden="true" />
              <div className="relative flex h-44 w-44 rotate-[-6deg] flex-col items-center justify-center rounded-full bg-[#dc2626] p-5 text-center shadow-2xl">
                <span className="font-display text-lg font-extrabold uppercase tracking-widest text-white">
                  {t.hero.promo.offer}
                </span>
                <span className="font-display mt-0.5 text-xs font-bold uppercase tracking-wide text-white/90">
                  {t.hero.promo.until}
                </span>
                <span className="my-1.5 h-px w-3/4 bg-white/40" aria-hidden="true" />
                <span className="font-display text-xl font-extrabold uppercase leading-tight text-white">
                  {t.hero.promo.free}
                </span>
                <span className="font-display mt-1 text-xs font-semibold uppercase tracking-wide text-white/90">
                  {t.hero.promo.withAny}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
